import { Controller } from "@hotwired/stimulus"

interface BBox {
  x: number
  y: number
  width: number
  height: number
  label?: string
  confidence?: number
  normalized?: boolean
}

const PALETTE = [
  "#06b6d4", // cyan-500
  "#f97316", // orange-500
  "#8b5cf6", // violet-500
  "#22c55e", // green-500
  "#ef4444", // red-500
  "#eab308", // yellow-500
  "#ec4899", // pink-500
  "#3b82f6", // blue-500
  "#14b8a6", // teal-500
  "#f59e0b", // amber-500
]

export default class extends Controller {
  static targets = ["image", "canvas", "toggle"]
  static values = {
    boxes: Array,
    visible: { type: Boolean, default: true },
  }

  declare imageTarget: HTMLImageElement
  declare canvasTarget: HTMLCanvasElement
  declare toggleTarget: HTMLButtonElement
  declare hasToggleTarget: boolean
  declare boxesValue: BBox[]
  declare visibleValue: boolean

  connect() {
    if (this.imageTarget.complete && this.imageTarget.naturalWidth > 0) {
      this.draw()
    } else {
      this.imageTarget.addEventListener("load", () => this.draw(), {
        once: true,
      })
    }
  }

  toggle() {
    this.visibleValue = !this.visibleValue
    this.canvasTarget.classList.toggle("hidden", !this.visibleValue)
    if (this.hasToggleTarget) {
      this.toggleTarget.textContent = this.visibleValue
        ? "HIDE BOXES"
        : "SHOW BOXES"
    }
  }

  private draw() {
    const img = this.imageTarget
    const canvas = this.canvasTarget

    // Match the canvas to the image's displayed size
    const rect = img.getBoundingClientRect()
    canvas.width = rect.width
    canvas.height = rect.height

    // Position canvas exactly over the image
    canvas.style.position = "absolute"
    canvas.style.top = `${img.offsetTop}px`
    canvas.style.left = `${img.offsetLeft}px`
    canvas.style.pointerEvents = "none"

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const imgW = img.naturalWidth
    const imgH = img.naturalHeight
    const dispW = rect.width
    const dispH = rect.height

    // Track unique labels for consistent coloring
    const labelColorMap = new Map<string, string>()
    let colorIdx = 0

    this.boxesValue.forEach((box) => {
      let x: number, y: number, w: number, h: number

      if (box.normalized) {
        // Normalized coords (0-1): multiply by display dimensions
        x = box.x * dispW
        y = box.y * dispH
        w = box.width * dispW
        h = box.height * dispH
      } else {
        if (!imgW || !imgH) return  // can't scale pixel coords without intrinsic size (e.g. SVG)
        // Pixel coords: scale from natural to display
        const scaleX = dispW / imgW
        const scaleY = dispH / imgH
        x = box.x * scaleX
        y = box.y * scaleY
        w = box.width * scaleX
        h = box.height * scaleY
      }

      // Pick color based on label (same label = same color)
      const labelKey = box.label || `__unlabeled_${colorIdx}`
      if (!labelColorMap.has(labelKey)) {
        labelColorMap.set(labelKey, PALETTE[colorIdx % PALETTE.length])
        colorIdx++
      }
      const color = labelColorMap.get(labelKey)!

      // Semi-transparent fill
      ctx.fillStyle = this.hexToRgba(color, 0.1)
      ctx.fillRect(x, y, w, h)

      // Border
      ctx.strokeStyle = color
      ctx.lineWidth = 2
      ctx.strokeRect(x, y, w, h)

      // Label badge
      if (box.label || box.confidence != null) {
        const labelParts: string[] = []
        if (box.label) labelParts.push(box.label.toUpperCase())
        if (box.confidence != null)
          labelParts.push(`${(box.confidence * 100).toFixed(0)}%`)
        const text = labelParts.join(" ")

        ctx.font = "bold 9px ui-monospace, monospace"
        const metrics = ctx.measureText(text)
        const padX = 4
        const padY = 2
        const badgeW = metrics.width + padX * 2
        const badgeH = 13

        // Position badge at top-left of box
        const badgeX = x
        const badgeY = Math.max(y - badgeH, 0)

        ctx.fillStyle = color
        ctx.fillRect(badgeX, badgeY, badgeW, badgeH)

        ctx.fillStyle = "#ffffff"
        ctx.fillText(text, badgeX + padX, badgeY + badgeH - padY - 1)
      }
    })
  }

  private hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }
}
